import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useComments } from '../hooks/useComments';
import { useCommentActions } from '../hooks/useCommentActions';
import { useStaffCommentReply } from '../hooks/useStaffCommentReply';
import { useAiReplySuggestions } from '../hooks/useAiReplySuggestions';
import { usePost } from '../hooks/usePost';
import { buildCommentTree } from '../utils/buildCommentTree';
import Toast from '../components/Toast';
import AiProductRecommendations, { appendProductBlock } from '../components/AiProductRecommendations';
import { savePostLoginRedirect } from '../utils/postLoginRedirect';

function ReplyBox({ commentId, replyText, onTextChange, onSubmit, onCancel, isSubmitting }) {
  const { data: suggestionsData, isLoading } = useAiReplySuggestions(commentId);
  const suggestions = suggestionsData?.suggestions ?? [];

  return (
    <div style={{
      marginTop: 10, padding: 14, background: '#f8fff8',
      borderRadius: 8, border: '1px solid #c8e6c9',
    }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{
          fontSize: 12, color: '#555', marginBottom: 6,
          display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500,
        }}>
          <span>✨</span>
          <span>AI Suggestions</span>
          {isLoading && <span style={{ color: '#aaa', fontWeight: 400 }}>fetching…</span>}
        </div>
        {suggestions.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onTextChange(s)}
                title={s}
                style={{
                  padding: '5px 12px', background: '#e8f5e9',
                  border: '1px solid #a5d6a7', borderRadius: 16,
                  fontSize: 12, color: '#2e7d32', cursor: 'pointer',
                  textAlign: 'left', lineHeight: 1.4,
                }}
              >
                {s.length > 90 ? s.slice(0, 90) + '…' : s}
              </button>
            ))}
          </div>
        ) : !isLoading ? (
          <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>
            No AI suggestions available
          </div>
        ) : null}
      </div>
      <AiProductRecommendations
        data={suggestionsData}
        isLoading={isLoading}
        onInsert={(block) => onTextChange(appendProductBlock(replyText, block))}
      />
      <textarea
        value={replyText}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder="Write your expert reply…"
        rows={3}
        style={{
          width: '100%', padding: 10, borderRadius: 6,
          border: '1px solid #ccc', fontSize: 14, resize: 'vertical',
          boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '8px 14px', background: '#f5f5f5',
            border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          style={{
            padding: '8px 14px',
            background: isSubmitting ? '#90caf9' : '#1976d2',
            color: '#fff', border: 'none', borderRadius: 6,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
        >
          {isSubmitting ? 'Sending…' : 'Publish reply'}
        </button>
      </div>
    </div>
  );
}

const Comments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routerState = location.state || {};
  const searchParams = new URLSearchParams(location.search);

  // The URL is the source of truth, so this page can be opened from a link such as a
  // notification: /comments?postId=<id>&commentId=<id>. The router state passed by the
  // Posts page is only a shortcut that saves fetching the post again.
  const postId = searchParams.get('postId') || routerState.postId;
  const targetCommentId = searchParams.get('commentId');
  const hasPostState = !!routerState.postId && routerState.postId === postId;
  const {
    data: fetchedPost,
    isLoading: isPostLoading,
    error: postError,
  } = usePost(postId, { enabled: !hasPostState });
  const {
    content,
    firstName,
    lastName,
    profilePic,
    createdAt,
    media,
  } = (hasPostState ? routerState : fetchedPost) || {};

  const containerRef = useRef(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [toast, setToast] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  const { approveComment } = useCommentActions();
  const staffReply = useStaffCommentReply(postId);
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [replyText, setReplyText] = useState('');

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    dataUpdatedAt,
    refetch
  } = useComments(postId, 10, dateFilter);

  // Flatten all pages of data
  const allComments = data?.pages.flatMap(page => page.items) || [];
  const commentTree = useMemo(() => buildCommentTree(allComments), [allComments]);

  // A link can only land on a comment once the user is signed in; send them to login
  // first and bring them back to this exact URL afterwards.
  useEffect(() => {
    if (!localStorage.getItem('accessToken')) {
      savePostLoginRedirect(`${location.pathname}${location.search}`);
      navigate('/login', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to and briefly highlight the comment named by ?commentId=
  const [highlightedId, setHighlightedId] = useState(null);
  const handledTargetRef = useRef(null);
  const retriedTargetRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const mountedAtRef = useRef(Date.now());

  useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

  useEffect(() => {
    if (!targetCommentId || !data || isLoading || isFetching) return;
    const targetKey = `${postId}:${targetCommentId}`;
    if (handledTargetRef.current === targetKey) return;

    const found = data.pages.some((page) =>
      page.items.some((c) => c.postCommentId === targetCommentId)
    );

    if (!found) {
      // Cached comments may predate the reply that triggered the link: refetch once
      // before concluding the comment is gone.
      if (dataUpdatedAt < mountedAtRef.current && retriedTargetRef.current !== targetKey) {
        retriedTargetRef.current = targetKey;
        refetch();
        return;
      }
      handledTargetRef.current = targetKey;
      setToast({
        message: 'The comment you opened is no longer available on this post.',
        type: 'warning',
      });
      return;
    }

    handledTargetRef.current = targetKey;
    setHighlightedId(targetCommentId);
    clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedId(null), 6000);
    // Wait a frame so the comment tree is in the DOM before scrolling to it.
    requestAnimationFrame(() => {
      document
        .getElementById(`comment-${targetCommentId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [data, dataUpdatedAt, isLoading, isFetching, postId, targetCommentId, refetch]);

  const staffBadgeLabel = (role) => {
    if (!role) return 'Staff';
    const r = String(role).toLowerCase();
    if (r === 'admin') return 'Admin';
    if (r === 'expert') return 'Expert';
    if (r === 'support') return 'Support';
    return 'Staff';
  };

  const canModifyComment = (comment) => {
    return comment.status === 'pending';
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return '#4CAF50';
      case 'rejected':
        return '#f44336';
      case 'pending':
        return '#FF9800';
      default:
        return '#999';
    }
  };

  // Helper function to get status badge text
  const getStatusBadgeText = (status) => {
    switch (status) {
      case 'approved':
        return '✓ Approved';
      case 'rejected':
        return '✗ Rejected';
      case 'pending':
        return '⏳ Pending';
      default:
        return status;
    }
  };

  const handleDateFilter = async () => {
    // Format dates for API (YYYY/MM/DD)
    const formatDate = (date) => {
      if (!date) return '';
      const d = new Date(date);
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
    };

    const fromDate = formatDate(startDate);
    const toDate = formatDate(endDate);

    console.log('Applying date filter:', { startDate, endDate, fromDate, toDate });

    // Validate dates
    if (!fromDate || !toDate) {
      setToast({
        message: 'Please select both start and end dates',
        type: 'warning'
      });
      return;
    }

    // Validate that start date is not after end date
    if (new Date(startDate) > new Date(endDate)) {
      setToast({
        message: 'Start date cannot be after end date',
        type: 'warning'
      });
      return;
    }

    // Show loading state
    setIsLoadingComments(true);
    
    try {
      // Apply the filter
      const newFilter = { fromDate, toDate };
      console.log('Setting date filter:', newFilter);
      setDateFilter(newFilter);
      
      // Refetch data with the new filter
      console.log('Refetching data with filter');
      const result = await refetch();
      console.log('Refetch result:', result);
      
      // Check if we got any comments back
      const filteredComments = result.data?.pages.flatMap(page => page.items) || [];
      console.log('Filtered comments count:', filteredComments.length);
      
      setToast({
        message: `Comments filtered successfully. Found ${filteredComments.length} comments.`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error filtering comments:', error);
      setToast({
        message: 'Failed to filter comments. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleClearFilters = async () => {
    console.log('Clearing date filters');
    setStartDate('');
    setEndDate('');
    
    // Only refetch if there was an active filter
    if (dateFilter) {
      console.log('Active filter detected, refetching data');
      setIsLoadingComments(true);
      try {
        setDateFilter(null);
        const result = await refetch();
        console.log('Refetch result after clearing filters:', result);
        
        // Check if we got all comments back
        const allComments = result.data?.pages.flatMap(page => page.items) || [];
        console.log('All comments count after clearing filters:', allComments.length);
        
        setToast({
          message: `Filters cleared. Showing all ${allComments.length} comments.`,
          type: 'success'
        });
      } catch (error) {
        console.error('Error clearing filters:', error);
        setToast({
          message: 'Failed to clear filters. Please try again.',
          type: 'error'
        });
      } finally {
        setIsLoadingComments(false);
      }
    } else {
      console.log('No active filter, no need to refetch');
    }
  };

  const handleApprove = (comment) => {
    // Validate that comment can be modified
    if (!canModifyComment(comment)) {
      const statusText = comment.status === 'approved' ? 'approved' : 'rejected';
      setToast({
        message: `This comment has already been ${statusText} and cannot be modified.`,
        type: 'warning'
      });
      return;
    }

    console.log('Selected comment for approval:', comment);
    setSelectedComment(comment);
    setActionType('approve');
    setShowConfirmDialog(true);
  };

  const handleReject = (comment) => {
    // Validate that comment can be modified
    if (!canModifyComment(comment)) {
      const statusText = comment.status === 'approved' ? 'approved' : 'rejected';
      setToast({
        message: `This comment has already been ${statusText} and cannot be modified.`,
        type: 'warning'
      });
      return;
    }

    console.log('Selected comment for rejection:', comment);
    setSelectedComment(comment);
    setActionType('reject');
    setShowConfirmDialog(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedComment) return;
    
    // Double-check that comment can still be modified
    if (!canModifyComment(selectedComment)) {
      setToast({
        message: 'This comment cannot be modified as it has already been processed.',
        type: 'warning'
      });
      setShowConfirmDialog(false);
      setSelectedComment(null);
      setActionType(null);
      setRemarks('');
      return;
    }
    
    // Validate remarks for reject action
    if (actionType === 'reject' && !remarks.trim()) {
      setToast({
        message: 'Please provide remarks for rejection',
        type: 'warning'
      });
      return;
    }

    try {
      const result = await approveComment.mutateAsync({
        postCommentId: selectedComment.postCommentId,
        status: actionType === 'approve' ? 'approved' : 'rejected',
        remarks: actionType === 'reject' ? remarks : undefined
      });

      if (result.success) {
        const commentPreview = selectedComment.comment.length > 50 
          ? selectedComment.comment.substring(0, 50) + '...' 
          : selectedComment.comment;
        
        setToast({
          message: `${actionType === 'approve' ? 'Comment approved' : 'Comment rejected'} successfully! "${commentPreview}"`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error processing comment:', error);
      setToast({
        message: error.message || 'Failed to process comment. Please try again.',
        type: 'error'
      });
    } finally {
      setShowConfirmDialog(false);
      setSelectedComment(null);
      setActionType(null);
      setRemarks('');
    }
  };

  const handleCancelAction = () => {
    setShowConfirmDialog(false);
    setSelectedComment(null);
    setActionType(null);
    setRemarks('');
  };

  const isSubmittingReplyRef = useRef(false);

  const handleSubmitStaffReply = async (parentPostCommentId) => {
    // Synchronous guard against double-clicks; `isPending` updates only after a re-render.
    if (isSubmittingReplyRef.current) return;
    if (!replyText.trim()) {
      setToast({ message: 'Please enter a reply', type: 'warning' });
      return;
    }
    isSubmittingReplyRef.current = true;
    try {
      await staffReply.mutateAsync({
        parentPostCommentId,
        comment: replyText.trim(),
      });
      setToast({ message: 'Reply published successfully', type: 'success' });
      setReplyTargetId(null);
      setReplyText('');
      await refetch();
    } catch (error) {
      setToast({
        message: error?.message || 'Failed to send reply',
        type: 'error',
      });
    } finally {
      isSubmittingReplyRef.current = false;
    }
  };

  const renderCommentNode = (node, depth) => {
    const comment = node.raw;
    const cid = comment.postCommentId;
    const maxThreadDepth = 2;
    const canReplyHere = depth < maxThreadDepth;
    const isStaff = !!comment.isStaffReply;
    const highlighted = cid === highlightedId;

    return (
      <div
        key={cid}
        id={`comment-${cid}`}
        style={{
          marginLeft: depth > 0 ? 18 : 0,
          paddingLeft: depth > 0 ? 14 : 0,
          borderLeft: depth > 0 ? '3px solid #c8e6c9' : 'none',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            padding: '20px',
            background: highlighted ? '#fff8e1' : isStaff ? '#f1f8e9' : '#f5f5f5',
            borderRadius: '8px',
            display: 'flex',
            position: 'relative',
            boxShadow: isStaff ? '0 1px 0 rgba(76,175,80,0.35)' : 'none',
            outline: highlighted ? '2px solid #ffb300' : '2px solid transparent',
            transition: 'background 0.4s ease, outline-color 0.4s ease',
          }}
        >
          <div
            style={{
              width: '6px',
              borderRadius: '6px 0 0 6px',
              marginRight: '12px',
              background: getStatusColor(comment.status),
            }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img
                  src={comment.profilePic || 'https://via.placeholder.com/40'}
                  alt="Profile"
                  style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                />
                <div>
                  <div style={{ fontWeight: 500, fontSize: '16px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>{`${comment.firstName || ''} ${comment.lastName || ''}`.trim() || 'User'}</span>
                    {isStaff && (
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: '#1b5e20',
                          background: '#dcedc8',
                          padding: '3px 8px',
                          borderRadius: 999,
                        }}
                      >
                        {staffBadgeLabel(comment.staffReplyRole)}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : ''}
                  </div>
                  <div
                    style={{
                      fontSize: '13px',
                      color: getStatusColor(comment.status),
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  >
                    {getStatusBadgeText(comment.status)}
                  </div>
                  {(comment.status === 'approved' || comment.status === 'rejected') && comment.updatedAt && (
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#999',
                        fontStyle: 'italic',
                      }}
                    >
                      {comment.status === 'approved' ? 'Approved' : 'Rejected'} on{' '}
                      {new Date(comment.updatedAt).toLocaleString()}
                      {comment.updatedBy && ` by ${comment.updatedBy}`}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {canReplyHere && (
                  <button
                    type="button"
                    onClick={() => {
                      if (replyTargetId === cid) {
                        setReplyTargetId(null);
                        setReplyText('');
                      } else {
                        setReplyTargetId(cid);
                        setReplyText('');
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      background: '#e3f2fd',
                      color: '#0d47a1',
                      border: '1px solid #90caf9',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {replyTargetId === cid ? 'Close reply' : 'Reply'}
                  </button>
                )}
                {canModifyComment(comment) ? (
                  <>
                    <button
                      type="button"
                      onClick={() => handleApprove(comment)}
                      disabled={approveComment.isPending}
                      style={{
                        padding: '6px 12px',
                        background: approveComment.isPending ? '#ccc' : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: approveComment.isPending ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {approveComment.isPending ? '⏳ Processing...' : '✓ Approve'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReject(comment)}
                      disabled={approveComment.isPending}
                      style={{
                        padding: '6px 12px',
                        background: approveComment.isPending ? '#ccc' : '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: approveComment.isPending ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {approveComment.isPending ? '⏳ Processing...' : '✗ Reject'}
                    </button>
                  </>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      background: comment.status === 'approved' ? '#e8f5e8' : '#ffebee',
                      color: comment.status === 'approved' ? '#2e7d32' : '#c62828',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      border: `1px solid ${comment.status === 'approved' ? '#c8e6c9' : '#ffcdd2'}`,
                    }}
                  >
                    {comment.status === 'approved' ? '✓ Already Approved' : '✗ Already Rejected'}
                  </div>
                )}
              </div>
            </div>
            <div style={{ color: '#444', fontSize: '14px', lineHeight: '1.5' }}>{comment.comment}</div>
            {comment.status === 'rejected' && comment.remarks && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: '#ffebee',
                  border: '1px solid #ffcdd2',
                  borderRadius: '6px',
                  borderLeft: '4px solid #f44336',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#c62828',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Rejection Reason:
                </div>
                <div style={{ fontSize: '13px', color: '#d32f2f', lineHeight: '1.4' }}>{comment.remarks}</div>
              </div>
            )}
          </div>
        </div>
        {replyTargetId === cid && (
          <ReplyBox
            commentId={cid}
            replyText={replyText}
            onTextChange={setReplyText}
            onSubmit={() => handleSubmitStaffReply(cid)}
            onCancel={() => { setReplyTargetId(null); setReplyText(''); }}
            isSubmitting={staffReply.isPending}
          />
        )}
        {node.children?.length > 0 && (
          <div style={{ marginTop: 4 }}>{node.children.map((ch) => renderCommentNode(ch, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#fafaf7' }}>
      <Sidebar active="comments" />
      <main 
        ref={containerRef}
        style={{ 
          flex: 1, 
          marginLeft: 280, 
          height: '100vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'row',
          gap: 32,
          padding: '2rem 2rem 2rem 32px',
        }}
      >
        {/* If no post is selected, show a friendly message */}
        {!postId ? (
          <section style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '80vh',
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 2px 16px #0002',
              padding: '48px 32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: 400,
              width: '100%',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🗂️</div>
              <h2 style={{ fontWeight: 600, fontSize: 24, marginBottom: 8, color: '#18492b' }}>
                No Post Selected
              </h2>
              <div style={{ color: '#666', fontSize: 16, marginBottom: 24 }}>
                Please select a post to view its comments.
              </div>
              <button
                onClick={() => (navigate ? navigate('/posts') : window.location.assign('/posts'))}
                style={{
                  padding: '12px 32px',
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 500,
                  boxShadow: '0 2px 8px #0001',
                  transition: 'background 0.2s',
                }}
              >
                Go to Posts
              </button>
            </div>
          </section>
        ) : (
          // Post Section (Left)
          <section style={{
            flex: '0 0 380px',
            background: '#fff',
            borderRadius: 10,
            padding: 24,
            boxShadow: '0 2px 8px #0001',
            marginBottom: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            minWidth: 320,
            maxWidth: 400,
            width: '100%',
            height: 'fit-content',
          }}>
            {isPostLoading && (
              <div style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>Loading post…</div>
            )}
            {postError && (
              <div style={{
                color: '#c62828', background: '#ffebee', borderRadius: 8,
                padding: '10px 12px', fontSize: 14, marginBottom: 16, width: '100%',
                boxSizing: 'border-box',
              }}>
                Couldn't load the post details. The comments are still shown.
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <img 
                src={profilePic || 'https://via.placeholder.com/48'} 
                alt="Profile" 
                style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} 
              />
              <div>
                <div style={{ fontWeight: 500, fontSize: 16, color: '#333' }}>
                  {firstName} {lastName}
                </div>
                <div style={{ fontSize: 13, color: '#666' }}>
                  {createdAt ? new Date(createdAt).toLocaleString() : ''}
                </div>
              </div>
            </div>
            <div style={{ color: '#666', fontSize: 14, lineHeight: 1.6, marginBottom: 16, whiteSpace: 'pre-wrap' }}>
              {isPostLoading ? '' : content || 'No content available'}
            </div>
            {media && media.length > 0 ? (
              <div style={{ 
                marginTop: 16, 
                width: '100%',
                position: 'relative'
              }}>
                {/* Media Carousel */}
                <div 
                  data-carousel="true"
                  style={{
                  display: 'flex',
                  overflowX: 'auto',
                  scrollSnapType: 'x mandatory',
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch',
                  msOverflowStyle: '-ms-autohiding-scrollbar',
                  padding: '4px 0',
                  margin: '0 -4px',
                  borderRadius: '8px',
                  /* Hide scrollbar for Chrome, Safari and Opera */
                  '::-webkit-scrollbar': {
                    display: 'none'
                  },
                  /* Hide scrollbar for IE, Edge and Firefox */
                  MsOverflowStyle: 'none',
                  scrollbarWidth: 'none'
                }}>
                  {media.filter(item => item.mediaType === 'image').map((item, index) => (
                    <div 
                      key={index}
                      style={{
                        flex: '0 0 auto',
                        width: '100%',
                        scrollSnapAlign: 'start',
                        padding: '0 4px',
                        boxSizing: 'border-box',
                        position: 'relative'
                      }}
                    >
                      <img
                        src={item.mediaUrl}
                        alt={`Post media ${index + 1}`}
                        style={{
                          width: '100%',
                          maxHeight: '300px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          display: 'block',
                          background: '#f8f8f8',
                        }}
                      />
                      {media.filter(item => item.mediaType === 'image').length > 1 && (
                        <div style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '12px',
                          background: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {index + 1}/{media.filter(item => item.mediaType === 'image').length}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Navigation Controls */}
                {media.filter(item => item.mediaType === 'image').length > 1 && (
                  <>
                    {/* Navigation Arrows */}
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '8px',
                      transform: 'translateY(-50%)',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      zIndex: 2
                    }}
                    onClick={() => {
                      const carousel = document.querySelector('[data-carousel]');
                      if (carousel) {
                        const scrollLeft = carousel.scrollLeft;
                        const itemWidth = carousel.scrollWidth / media.filter(item => item.mediaType === 'image').length;
                        const currentIndex = Math.round(scrollLeft / itemWidth);
                        const prevIndex = Math.max(0, currentIndex - 1);
                        carousel.scrollTo({
                          left: prevIndex * itemWidth,
                          behavior: 'smooth'
                        });
                      }
                    }}
                    >
                      <span style={{ fontSize: '18px', color: '#333', lineHeight: 0 }}>‹</span>
                    </div>
                    
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      right: '8px',
                      transform: 'translateY(-50%)',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      zIndex: 2
                    }}
                    onClick={() => {
                      const carousel = document.querySelector('[data-carousel]');
                      if (carousel) {
                        const scrollLeft = carousel.scrollLeft;
                        const itemWidth = carousel.scrollWidth / media.filter(item => item.mediaType === 'image').length;
                        const currentIndex = Math.round(scrollLeft / itemWidth);
                        const nextIndex = Math.min(media.filter(item => item.mediaType === 'image').length - 1, currentIndex + 1);
                        carousel.scrollTo({
                          left: nextIndex * itemWidth,
                          behavior: 'smooth'
                        });
                      }
                    }}
                    >
                      <span style={{ fontSize: '18px', color: '#333', lineHeight: 0 }}>›</span>
                    </div>
                    
                    {/* Navigation Dots */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '6px',
                      marginTop: '12px'
                    }}>
                      {media.filter(item => item.mediaType === 'image').map((_, index) => (
                        <div
                          key={index}
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#ccc',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, background-color 0.2s',
                            ':hover': {
                              transform: 'scale(1.2)',
                              background: '#999'
                            }
                          }}
                          onClick={() => {
                            const carousel = document.querySelector('[data-carousel]');
                            if (carousel) {
                              const scrollWidth = carousel.scrollWidth;
                              const itemCount = media.filter(item => item.mediaType === 'image').length;
                              const itemWidth = scrollWidth / itemCount;
                              carousel.scrollTo({
                                left: index * itemWidth,
                                behavior: 'smooth'
                              });
                            }
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : isPostLoading ? null : (
              <div style={{
                marginTop: 16,
                padding: '12px',
                background: '#f5f5f5',
                borderRadius: '8px',
                color: '#666',
                fontSize: '14px',
                textAlign: 'center',
                width: '100%',
              }}>
                No media available
              </div>
            )}
          </section>
        )}

        {/* Comments Section (Right) */}
        {postId && (
        <section style={{
          flex: 1,
          background: '#fff',
          borderRadius: 10,
          padding: 24,
          boxShadow: '0 2px 8px #0001',
          minWidth: 320,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 4rem)',
          overflowY: 'auto',
        }}>
          <h2 style={{ fontWeight: 600, fontSize: 22, marginBottom: 18, color: '#18492b' }}>
            Comments
          </h2>
          
          {/* Status Summary */}
          {allComments.length > 0 && (
            <div style={{
              marginBottom: 20,
              padding: 16,
              background: '#f8f9fa',
              borderRadius: 8,
              border: '1px solid #e9ecef',
              display: 'flex',
              alignItems: 'center',
              gap: 24,
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: '#FF9800'
                }}></span>
                <span style={{ fontSize: 14, color: '#666' }}>
                  Pending: {allComments.filter(c => c.status === 'pending').length}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: '#4CAF50'
                }}></span>
                <span style={{ fontSize: 14, color: '#666' }}>
                  Approved: {allComments.filter(c => c.status === 'approved').length}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: '#f44336'
                }}></span>
                <span style={{ fontSize: 14, color: '#666' }}>
                  Rejected: {allComments.filter(c => c.status === 'rejected').length}
                </span>
              </div>
              <div style={{ 
                marginLeft: 'auto',
                fontSize: 14,
                fontWeight: 500,
                color: '#18492b'
              }}>
                Total: {allComments.length}
              </div>
            </div>
          )}
          {/* Date Filter Section - improved UX */}
          <div style={{
            marginBottom: 20,
            padding: 16,
            background: '#f5f5f5',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label htmlFor="startDate" style={{ marginRight: 8, fontSize: 14 }}>Start Date:</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  padding: 8,
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  fontSize: 14,
                  marginRight: 16,
                }}
              />
              <label htmlFor="endDate" style={{ marginRight: 8, fontSize: 14 }}>End Date:</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  padding: 8,
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  fontSize: 14,
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleDateFilter}
                disabled={isLoadingComments}
                style={{
                  padding: '8px 16px',
                  background: isLoadingComments ? '#a5d6a7' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: isLoadingComments ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {isLoadingComments ? 'Applying...' : 'Apply Filter'}
              </button>
              <button
                onClick={handleClearFilters}
                disabled={isLoadingComments}
                style={{
                  padding: '8px 16px',
                  background: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  cursor: isLoadingComments ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 500,
                  opacity: isLoadingComments ? 0.7 : 1,
                }}
              >
                Clear
              </button>
            </div>
          </div>
          {dateFilter && (
              <div style={{ 
                marginTop: 8, 
                padding: '6px 12px', 
                background: '#e3f2fd', 
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 14,
                color: '#0d47a1',
                border: '1px solid #bbdefb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ 
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#1976d2'
                  }}></span>
                  <span>
                    Filtering comments from {dateFilter.fromDate} to {dateFilter.toDate}
                  </span>
                </div>
                <button
                  onClick={handleClearFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 16,
                    color: '#1976d2',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    transition: 'background-color 0.2s'
                  }}
                  title="Clear filter"
                >
                  ×
                </button>
              </div>
            )}
          {/* Only swap the list for a spinner while comments are really loading. Replying / approving / rejecting
              must NOT unmount the list - doing so collapses the scroll area and drops the moderator back at the
              top. Those actions show their own progress on the button / dialog that started them. */}
          {isLoading || isLoadingComments ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16
            }}>
              <div style={{
                width: 40,
                height: 40,
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
              <div>Loading comments...</div>
            </div>
          ) : error ? (
            <div style={{ 
              color: '#f44336', 
              textAlign: 'center', 
              padding: '30px 20px',
              background: '#ffebee',
              borderRadius: 8,
              margin: '20px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12
            }}>
              <div style={{ fontSize: 24 }}>⚠️</div>
              <div style={{ fontWeight: 500 }}>Error loading comments</div>
              <div>{error.message}</div>
              <button
                onClick={() => refetch()}
                style={{
                  marginTop: 8,
                  padding: '8px 16px',
                  background: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Try Again
              </button>
            </div>
          ) : allComments.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px', 
              color: '#666',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16
            }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
                color: '#999'
              }}>
                🔍
              </div>
              <div>
                {dateFilter ? (
                  <>
                    <div style={{ fontWeight: 500, marginBottom: 8 }}>No comments found for the selected date range</div>
                    <div style={{ fontSize: 14 }}>Try adjusting your filter criteria or <button 
                      onClick={handleClearFilters}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: '#2196F3',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: 14
                      }}
                    >
                      clear filters
                    </button></div>
                  </>
                ) : 'No comments found for this post'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {commentTree.map((node) => renderCommentNode(node, 0))}
            </div>
          )}

          {hasNextPage && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                style={{
                  padding: '8px 16px',
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: isFetchingNextPage ? 0.7 : 1,
                }}
              >
                {isFetchingNextPage ? 'Loading more...' : 'Load More'}
              </button>
            </div>
          )}
        </section>
        )}

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
            onClick={approveComment.isPending ? undefined : handleCancelAction}
          >
            <div 
              style={{
                background: 'white',
                padding: '24px',
                borderRadius: '12px',
                maxWidth: '400px',
                width: '90%',
                textAlign: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', color: '#333' }}>
                Confirm {actionType === 'approve' ? 'Approval' : 'Rejection'}
              </h3>
              <p style={{ marginBottom: '24px', color: '#666', lineHeight: 1.5 }}>
                Are you sure you want to {actionType} this comment?
                <br />
                <small style={{ color: '#999', fontSize: '12px' }}>
                  This action will be recorded under your name: <strong>{localStorage.getItem('userName') || 'User'}</strong>
                </small>
              </p>
              {actionType === 'reject' && (
                <div style={{ marginBottom: '24px' }}>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Please provide remarks for rejection"
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      marginBottom: '12px',
                      resize: 'vertical',
                    }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={handleCancelAction}
                  disabled={approveComment.isPending}
                  style={{
                    padding: '8px 24px',
                    background: approveComment.isPending ? '#e0e0e0' : '#f5f5f5',
                    color: approveComment.isPending ? '#999' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: approveComment.isPending ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: approveComment.isPending ? 0.7 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAction}
                  disabled={approveComment.isPending}
                  style={{
                    padding: '8px 24px',
                    background: approveComment.isPending ? '#ccc' : (actionType === 'approve' ? '#4CAF50' : '#f44336'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: approveComment.isPending ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: approveComment.isPending ? 0.7 : 1,
                  }}
                >
                  {approveComment.isPending ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </main>
    </div>
  );
};

export default Comments; 